// Ticket Update Service
// Automatically updates ticket statuses based on real football data

import footballDataService from './footballDataService';
import firebaseService from './firebaseService';

class TicketUpdateService {
  constructor() {
    this.updateInterval = null;
    this.isUpdating = false;
    this.updateCallbacks = [];
  }

  // Start automatic ticket updates
  startAutoUpdate(intervalMinutes = 5) {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    const intervalMs = intervalMinutes * 60 * 1000;
    this.updateInterval = setInterval(() => {
      this.updateAllTickets();
    }, intervalMs);

    console.log(`Ticket auto-update started (every ${intervalMinutes} minutes)`);

    // Initial update
    this.updateAllTickets();
  }

  // Stop automatic updates
  stopAutoUpdate() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
      console.log('Ticket auto-update stopped');
    }
  }

  // Add callback for update notifications
  onUpdate(callback) {
    if (typeof callback === 'function') {
      this.updateCallbacks.push(callback);
    }
  }

  // Remove update callback
  offUpdate(callback) {
    this.updateCallbacks = this.updateCallbacks.filter(cb => cb !== callback);
  }

  // Notify all callbacks
  notifyCallbacks(updatedTickets) {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(updatedTickets);
      } catch (error) {
        console.error('Error in update callback:', error);
      }
    });
  }

  // Update all tickets from Firebase
  async updateAllTickets() {
    if (this.isUpdating) {
      console.log('Update already in progress, skipping...');
      return;
    }

    this.isUpdating = true;

    try {
      console.log('Starting ticket update process...');

      // Get all matches from Firebase
      const allMatches = await firebaseService.getAllMatches();

      if (!allMatches || allMatches.length === 0) {
        console.log('No tickets found to update');
        this.isUpdating = false;
        return;
      }

      console.log(`Found ${allMatches.length} tickets to check for updates`);

      // Filter tickets that need updating (upcoming or live matches)
      const ticketsToUpdate = allMatches.filter(ticket =>
        ticket.status === 'upcoming' || ticket.status === 'live'
      );

      if (ticketsToUpdate.length === 0) {
        console.log('No tickets need updating');
        this.isUpdating = false;
        return;
      }

      console.log(`Updating ${ticketsToUpdate.length} tickets...`);

      // Update tickets using the backend API
      const result = await footballDataService.updateTicketStatuses(ticketsToUpdate);

      if (result.updatedTickets && result.updatedTickets.length > 0) {
        const actuallyUpdated = [];

        // Update tickets in Firebase if status changed
        for (const updatedTicket of result.updatedTickets) {
          const originalTicket = ticketsToUpdate.find(t => t.id === updatedTicket.id);

          if (originalTicket && this.hasTicketChanged(originalTicket, updatedTicket)) {
            try {
              await firebaseService.updateMatch(updatedTicket.id, updatedTicket);
              actuallyUpdated.push(updatedTicket);
              console.log(`Updated ticket ${updatedTicket.id}: ${originalTicket.status} -> ${updatedTicket.status}`);
            } catch (error) {
              console.error(`Failed to update ticket ${updatedTicket.id} in Firebase:`, error);
            }
          }
        }

        if (actuallyUpdated.length > 0) {
          console.log(`Successfully updated ${actuallyUpdated.length} tickets`);
          this.notifyCallbacks(actuallyUpdated);
        } else {
          console.log('No tickets were actually updated');
        }
      }

    } catch (error) {
      console.error('Error updating tickets:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  // Check if ticket data has changed
  hasTicketChanged(original, updated) {
    return (
      original.status !== updated.status ||
      original.actualScore !== updated.actualScore ||
      original.result !== updated.result
    );
  }

  // Manual update for specific tickets
  async updateSpecificTickets(ticketIds) {
    try {
      console.log(`Manually updating ${ticketIds.length} specific tickets...`);

      const allMatches = await firebaseService.getAllMatches();
      const ticketsToUpdate = allMatches.filter(ticket =>
        ticketIds.includes(ticket.id) &&
        (ticket.status === 'upcoming' || ticket.status === 'live')
      );

      if (ticketsToUpdate.length === 0) {
        console.log('No matching tickets found to update');
        return [];
      }

      const result = await footballDataService.updateTicketStatuses(ticketsToUpdate);
      const actuallyUpdated = [];

      for (const updatedTicket of result.updatedTickets) {
        const originalTicket = ticketsToUpdate.find(t => t.id === updatedTicket.id);

        if (originalTicket && this.hasTicketChanged(originalTicket, updatedTicket)) {
          await firebaseService.updateMatch(updatedTicket.id, updatedTicket);
          actuallyUpdated.push(updatedTicket);
        }
      }

      if (actuallyUpdated.length > 0) {
        this.notifyCallbacks(actuallyUpdated);
      }

      return actuallyUpdated;
    } catch (error) {
      console.error('Error updating specific tickets:', error);
      throw error;
    }
  }

  // Get update status
  getStatus() {
    return {
      isUpdating: this.isUpdating,
      isAutoUpdateActive: !!this.updateInterval,
      callbackCount: this.updateCallbacks.length
    };
  }

  // Force immediate update
  forceUpdate() {
    return this.updateAllTickets();
  }
}

// Create and export singleton instance
const ticketUpdateService = new TicketUpdateService();
export default ticketUpdateService;
